import type { OutboxOp } from './db';
import { getDb } from './db';

interface OpStore {
  getAll(): Promise<OutboxOp[]>;
  put(value: OutboxOp): Promise<number>;
}
export const opBody = (op: OutboxOp): Record<string, unknown> =>
  op.body && typeof op.body === 'object' ? (op.body as Record<string, unknown>) : {};
const text = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
export const leadIdentity = (op: OutboxOp, all: OutboxOp[]): string | undefined => {
  const b = opBody(op);
  if (op.path === '/api/leads') return text(b.id);
  const direct = op.path.match(/^\/api\/leads\/([^/]+)/)?.[1] ?? text(b.lead_id);
  if (direct) return direct;
  const visitId = text(b.visit_id) ?? op.path.match(/^\/api\/visits\/([^/]+)/)?.[1];
  return text(
    opBody(all.find((p) => p.path === '/api/visits' && opBody(p).id === visitId) ?? op).lead_id,
  );
};
export const linkOp = (op: OutboxOp, all: OutboxOp[]): OutboxOp => {
  const lead = op.local_lead_id ?? leadIdentity(op, all);
  const b = opBody(op);
  const parent =
    lead && !(op.path === '/api/leads' && op.method === 'POST')
      ? all.find(
          (p) =>
            p.id !== op.id &&
            p.path === '/api/leads' &&
            p.method === 'POST' &&
            opBody(p).id === lead,
        )
      : undefined;
  const visit =
    typeof b.visit_id === 'string'
      ? all.find(
          (p) =>
            p.id !== op.id &&
            p.path === '/api/visits' &&
            p.method === 'POST' &&
            opBody(p).id === b.visit_id,
        )
      : undefined;
  return {
    ...op,
    local_lead_id: lead,
    depends_on: [
      ...new Set([...(op.depends_on ?? []), ...[parent, visit].flatMap((p) => (p ? [p.id] : []))]),
    ],
  };
};
/** Called in each enqueue transaction, and before replay to backfill legacy queue rows. */
export const reconcileDependencies = async (store: OpStore): Promise<OutboxOp[]> => {
  const all = (await store.getAll()).map((op, _, rows) => linkOp(op, rows));
  // FIFO dependencies point backward; repeated passes also tolerate restored seq ordering.
  for (let pass = 0; pass <= all.length; pass++) {
    let changed = false;
    for (let i = 0; i < all.length; i++) {
      const op = all[i]!;
      const parents = all.filter((p) => op.depends_on?.includes(p.id));
      const blocked = parents.find((p) => p.status === 'parked' || p.status === 'failed');
      if (blocked && op.park_reason !== 'conflict') {
        if (op.status !== 'parked' || op.existing_lead_id !== blocked.existing_lead_id)
          changed = true;
        all[i] = {
          ...op,
          status: 'parked',
          park_reason: 'dependency',
          existing_lead_id: blocked.existing_lead_id,
          last_error: 'Waiting for the original draft to be resolved',
        };
      } else if (!parents.length && op.park_reason === 'dependency') {
        all[i] = {
          ...op,
          status: 'pending',
          park_reason: undefined,
          existing_lead_id: undefined,
          last_error: undefined,
        };
        changed = true;
      }
    }
    if (!changed) break;
  }
  for (const op of all) await store.put(op);
  return all;
};
export const nextReadyOp = async (): Promise<OutboxOp | undefined> => {
  const tx = (await getDb()).transaction('outbox', 'readwrite');
  const all = await reconcileDependencies(tx.store);
  const op = all.find(
    (o) => o.status === 'pending' && !all.some((p) => o.depends_on?.includes(p.id)),
  );
  await tx.done;
  return op;
};
export const parkConflict = async (seq: number, existing: string): Promise<void> => {
  const tx = (await getDb()).transaction('outbox', 'readwrite');
  const op = await tx.store.get(seq);
  if (op)
    await tx.store.put({
      ...op,
      status: 'parked',
      park_reason: 'conflict',
      existing_lead_id: existing,
      last_error: 'Already exists — open existing lead / edit draft',
      attempts: op.attempts + 1,
    });
  await reconcileDependencies(tx.store);
  await tx.done;
};
